<template>
  <div class="c-create-new-report">
    <v-container fluid>
      <v-row>
        <!-- Task selector -->
        <v-col cols="6" md="4" lg="3" v-if="tasks.length">
          <v-select
            :disabled="tasks.length === 1"
            label="Select task for report"
            v-model="value.Task_ID"
            :items="tasks"
            item-text="taskName"
            item-value="taskId"
            @change="setEmployeeForTask"
          >
          </v-select>
        </v-col>
        <!-- /Task selector -->
        <!-- Employee selector -->
        <v-col cols="6" md="4" lg="3">
          <v-select
            disabled
            v-model="value.Employee_ID"
            :items="employeeList"
            label="Employee"
            item-text="full_name"
            item-value="Employee_ID"
          ></v-select>
        </v-col>
        <!-- /Employee selector -->
        <!-- Start date picker -->
        <v-col cols="6" md="4" lg="3">
          <DatePicker
            v-model="value.Start_Date"
            label="Start date"
          />
        </v-col>
        <!-- /Start date picker -->
        <!-- Report duration -->
        <v-col cols="6" md="4" lg="3">
          <v-menu
            ref="reportDuration"
            v-model="reportDuration"
            :close-on-content-click="false"
            :nudge-right="40"
            :return-value.sync="value.Real_Dur"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="value.Real_Dur"
                label="Report duration"
                prepend-icon="mdi-clock-time-four-outline"
                readonly
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-time-picker
              v-if="reportDuration"
              v-model="value.Real_Dur"
              format="24hr"
              :allowed-minutes="allowedMinutes"
              @click:minute="setRealDur"
            ></v-time-picker>
          </v-menu>
        </v-col>
        <!-- /Report duration -->
        <!-- Paid duration -->
        <v-col cols="6" md="4" lg="3">
          <v-menu
            ref="paidDuration"
            v-model="paidDuration"
            :close-on-content-click="false"
            :nudge-right="40"
            :return-value.sync="value.Report_Dur"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="value.Report_Dur"
                label="Paid duration"
                prepend-icon="mdi-clock-time-four-outline"
                readonly
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-time-picker
              v-if="paidDuration"
              v-model="value.Report_Dur"
              format="24hr"
              :allowed-minutes="allowedMinutes"
              @click:minute="setReportDur"
            ></v-time-picker>
          </v-menu>
        </v-col>
        <!-- /Paid duration -->
        <!-- Invoice duration -->
        <v-col cols="6" md="4" lg="3">
          <v-menu
            ref="invoiceDuration"
            v-model="invoiceDuration"
            :close-on-content-click="false"
            :nudge-right="40"
            :return-value.sync="value.Invoice_Dur"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="value.Invoice_Dur"
                label="Invoice duration"
                prepend-icon="mdi-clock-time-four-outline"
                readonly
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-time-picker
              v-if="invoiceDuration"
              v-model="value.Invoice_Dur"
              format="24hr"
              :allowed-minutes="allowedMinutes"
              @click:minute="setInvoiceDur"
            ></v-time-picker>
          </v-menu>
        </v-col>
        <!-- /Invoice duration -->
        <!-- Activity type selector -->
        <v-col cols="6" md="4" lg="3">
          <v-select
            label="Activity type"
            v-model="value.Activity_Type"
            :items="activityTypes"
            item-text="Description"
            item-value="Activity_Type"
          ></v-select>
        </v-col>
        <!-- /Activity type selector -->
        <!-- Description -->
        <v-col cols="12" class="description-col">
          <div class="description-wrapper">
            <DescriptionWithSpellCheck
              label="Description"
              v-model="description"
            />

            <BadWords
              :description="value.Description"
              :hours="value.Real_Dur"
              :bad-words="badWords"
            />
          </div>
        </v-col>
        <!-- /Description -->
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { sharedReportingMixin } from '@/mixins/sharedReportingMixin'

export default {
  name: 'CreateNewReport',
  mixins: [sharedReportingMixin],
  props: ['value', 'tasks'],
  components: {
    DescriptionWithSpellCheck: () => import('@/components/reporting/DescriptionWithSpellCheck.vue'),
    DatePicker: () => import('@/components/global/DatePicker.vue'),
    BadWords: () => import('@/components/global/BadWords.vue')
  },
  data () {
    return {
      searchEmployee: '',
      reportDuration: false,
      paidDuration: false,
      invoiceDuration: false,
      badWords: []
    }
  },
  computed: {
    description: {
      get () {
        return this.value.Description
      },
      set (val) {
        this.value.Description = val
        this.$emit('input', this.value)
      }
    },
    activityTypes () {
      return this.$store.getters.getActivityTypes
    },
    usersOnProject () {
      const usersOnProject = this.$store.getters.getUsersOnProject
      return this.$store.getters.getEmployee.filter(item => usersOnProject.indexOf(item.employee_id) > -1).map(e => ({
        ...e,
        Employee_ID: e.employee_id
      }))
    },
    employeeList () {
      if (this.searchEmployee) {
        const reg = new RegExp(this.searchEmployee.toLowerCase(), 'gi')
        return this.usersOnProject.filter(item => {
          reg.lastIndex = 0
          return reg.test(item.full_name.toLowerCase())
        })
      } else return this.usersOnProject
    }
  },
  async mounted () {
    this.setEmployeeForTask(this.value.Task_ID)
    this.badWords = await this.getBadWords()
  },
  methods: {
    setEmployeeForTask (e) {
      if (this.tasks.length) {
        const foundTask = this.tasks.find(item => item.taskId === e)
        if (foundTask) {
          this.value.Employee_ID = foundTask.employeeId
        } else {
          this.$store.dispatch('setError', { errorMsg: 'Task not found' })
        }
      }
    },
    setReportDur () {
      this.$refs.paidDuration.save(this.value.Report_Dur)
      if (!this.value.Real_Dur) {
        this.value.Real_Dur = this.value.Report_Dur
      }
      this.value.Invoice_Dur = this.value.Report_Dur
    },
    setRealDur () {
      this.$refs.reportDuration.save(this.value.Real_Dur)
      if (!this.value.Report_Dur || !this.value.Invoice_Dur) {
        this.value.Report_Dur = this.value.Real_Dur
        this.value.Invoice_Dur = this.value.Real_Dur
      }
    },
    setInvoiceDur () {
      this.$refs.invoiceDuration.save(this.value.Invoice_Dur)
      if (!this.value.Report_Dur || !this.value.Real_Dur) {
        this.value.Report_Dur = this.value.Invoice_Dur
        this.value.Real_Dur = this.value.Invoice_Dur
      }
    },
    allowedMinutes (m) {
      return m === 30 || m === 0
    }
  }
}
</script>

<style lang="scss">
.c-create-new-report {
  .description-col {
    .description-wrapper {
      position: relative;
    }

    .bad-words-indicator {
      position: absolute;
      top: 10px;
      right: 10px;
      z-index: 2;
    }
  }
}
</style>
